import { isEmpty } from 'lodash-es'

import { Badge } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'

import { dashOrSpaceToUnderscore, getOrderDisplayName } from 'Utilities'

import {
  ORDER_STATUS_BADGE_COLOR_MAP,
  ORDER_STATUS_BADGE_VARIANT_MAP,
  ORDER_STATUS_TRANSLATION_IDS,
} from 'Constants/orderStatus'
import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'

import {
  getReturnCasePortalStatusBadgeColor,
  getReturnCasePortalStatusBadgeVariant,
  getReturnCasePortalStatusTranslationId,
} from 'Portal/src/utilities/getReturnCasePortalStatus/getReturnCasePortalStatus.utils'

import type {
  GlobalSearchAutocompleteInlineHighlightProps,
  GlobalSearchAutocompleteItemProps,
} from './GlobalSearch.types'
import {
  getHighlightFieldLabel,
  parseOrderDisplay,
  parseReturnCaseDisplay,
  stripHtmlFromString,
} from './GlobalSearch.utils'
import { GlobalSearchOrgLabel } from './GlobalSearchOrgLabel'

const InlineHighlight = ({
  intl,
  highlightItemType,
  snippet,
}: GlobalSearchAutocompleteInlineHighlightProps) => {
  if (!highlightItemType || !snippet) {
    return null
  }

  return (
    <>
      {' '}·{' '}
      {intl.formatMessage({ defaultMessage: highlightItemType, id: highlightItemType })}
      :{' '}
      <span dangerouslySetInnerHTML={{ __html: snippet }} />
    </>
  )
}

export const GlobalSearchAutocompleteItem = (
  props: GlobalSearchAutocompleteItemProps,
) => {
  const {
    suggestion,
    onSuggestionItemClickHandler,
    onDirectItemClickHandler,
    intl,
  } = props
  const {
    type,
    snippet,
    highlights,
    display,
    category,
    organisation_id: organisationId,
    organisation_name: organisationName,
  } = suggestion
  const { formatMessage } = intl

  const renderSuggestionTemplate = () => {
    if (onSuggestionItemClickHandler) {
      const categoryIcon = PORTAL_CONTENT_MODEL[category].icon

      return (
        <button
          aria-label={stripHtmlFromString(snippet)}
          className="gs-autocomplete-suggestion-item"
          onClick={onSuggestionItemClickHandler}
          type="button"
        >
          <Icon className="px-1" icon={categoryIcon} />

          <div className="d-flex flex-column">
            <div
              dangerouslySetInnerHTML={{ __html: snippet }}
              className="gs-autocomplete-suggestion-item__primary-display"
            />

            <GlobalSearchOrgLabel
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>
        </button>
      )
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: snippet }}
        aria-label={stripHtmlFromString(snippet)}
        className="gs-autocomplete-suggestion-item"
      />
    )
  }

  const renderDirectItemTemplate = () => {
    if (isEmpty(highlights)) {
      return renderSuggestionTemplate()
    }

    const [highlight] = highlights
    const highlightItemType = getHighlightFieldLabel(highlight.field)

    const categoryIcon = PORTAL_CONTENT_MODEL[category].icon

    const translatedHighlightType = intl.formatMessage({
      defaultMessage: highlightItemType,
      id: highlightItemType,
    })

    return (
      <button
        aria-label={stripHtmlFromString(snippet)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <Icon className="px-1" icon={categoryIcon} />

        <div className="d-flex flex-column w-100">
          <div
            dangerouslySetInnerHTML={{ __html: snippet }}
            className="gs-autocomplete-direct-item__primary-display"
          />

          {highlight.snippet && (
            <div className="gs-autocomplete-direct-item__secondary-display">
              {translatedHighlightType}:{' '}
              <span dangerouslySetInnerHTML={{ __html: highlight.snippet }} />
              <GlobalSearchOrgLabel
                inline
                organisationId={organisationId}
                organisationName={organisationName}
              />
            </div>
          )}
        </div>
      </button>
    )
  }

  const renderOrderItemTemplate = () => {
    const parsedDisplay = parseOrderDisplay(display)
    const displayName = getOrderDisplayName(
      parsedDisplay.type,
      parsedDisplay.name,
    )
    const formattedStatus = dashOrSpaceToUnderscore(parsedDisplay.status)

    const renderDisplayName = () => {
      if (typeof displayName === 'string') {
        return formatMessage({ id: displayName })
      }

      return `${formatMessage({ id: displayName.type })}: ${displayName.name}`
    }

    const [highlight] = highlights
    const highlightItemType = highlight
      ? getHighlightFieldLabel(highlight.field)
      : null

    return (
      <button
        aria-label={stripHtmlFromString(parsedDisplay.highlight)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <div className="d-flex flex-column w-100">
          <div className="d-flex align-items-center justify-content-between">
            <div
              dangerouslySetInnerHTML={{ __html: renderDisplayName() }}
              className="gs-autocomplete-direct-item__primary-display"
            />

            <Badge
              className="me-1"
              color={ORDER_STATUS_BADGE_COLOR_MAP[formattedStatus]}
              variant={ORDER_STATUS_BADGE_VARIANT_MAP[formattedStatus]}
            >
              {formatMessage({
                id: ORDER_STATUS_TRANSLATION_IDS[formattedStatus],
              })}
            </Badge>
          </div>

          <div className="gs-autocomplete-direct-item__secondary-display">
            {formatMessage({ id: 'Request_id' })}:{' '}
            <span
              dangerouslySetInnerHTML={{ __html: parsedDisplay.highlight }}
            />
            <InlineHighlight
              highlightItemType={highlightItemType}
              intl={intl}
              snippet={highlight?.snippet}
            />
            <GlobalSearchOrgLabel
              inline
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>
        </div>
      </button>
    )
  }

  const renderReturnCaseItemTemplate = () => {
    const parsedDisplay = parseReturnCaseDisplay(display)
    const displayName = `${formatMessage({ id: 'Returns' })}: ${
      parsedDisplay.name
    }`

    const [highlight] = highlights
    const highlightItemType = highlight
      ? getHighlightFieldLabel(highlight.field)
      : null

    return (
      <button
        aria-label={stripHtmlFromString(parsedDisplay.highlight)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <div className="d-flex flex-column w-100">
          <div className="d-flex align-items-center justify-content-between">
            <div
              dangerouslySetInnerHTML={{ __html: displayName }}
              className="gs-autocomplete-direct-item__primary-display"
            />

            <Badge
              className="me-1"
              color={getReturnCasePortalStatusBadgeColor(
                parsedDisplay.portalStatus,
              )}
              variant={getReturnCasePortalStatusBadgeVariant(
                parsedDisplay.portalStatus,
              )}
            >
              {formatMessage({
                id: getReturnCasePortalStatusTranslationId(
                  parsedDisplay.portalStatus,
                ),
              })}
            </Badge>
          </div>

          <div className="gs-autocomplete-direct-item__secondary-display">
            {formatMessage({ id: 'Return_id' })}:{' '}
            <span
              dangerouslySetInnerHTML={{ __html: parsedDisplay.highlight }}
            />
            <InlineHighlight
              highlightItemType={highlightItemType}
              intl={intl}
              snippet={highlight?.snippet}
            />
            <GlobalSearchOrgLabel
              inline
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>
        </div>
      </button>
    )
  }

  const renderTemplate = () => {
    if (type === 'direct' && category === 'return_cases') {
      return renderReturnCaseItemTemplate()
    }

    if (type === 'direct' && category === 'caseitems') {
      return renderOrderItemTemplate()
    }

    if (type === 'direct') {
      return renderDirectItemTemplate()
    }

    return renderSuggestionTemplate()
  }

  return renderTemplate()
}
